<template>
  <v-container class="pa-0 form-renderer">
    <v-stepper
      v-if="isFormLoaded"
      v-model="selectedStepOrder"
      @update:modelValue="onStepChange"
    >
      <!--Header/Steps-->
      <v-stepper-header>
        <template
          v-for="step in form?.steps"
          :key="`${step.uniqueId}-step`"
        >
          <v-stepper-item
            :complete="stepCompleted(step)"
            :edit-icon="'$complete'"
            :value="step.sortOrder"
            :color="isGreen(step.sortOrder) ? 'success' : 'primary'"
            :editable="devTesting || isEditable(step.sortOrder)"
            :rules="[() => step.valid]"
          >
            <div
              :class="{
                'step-name-green': isGreen(step.sortOrder) && step.valid,
              }"
            >
              {{ getName(step) }}
            </div>
          </v-stepper-item>
          <v-divider
            v-if="!isLastStep(step.sortOrder)"
            :key="`${step.uniqueId}-div`"
            :class="{
              'divider-green':
                step.sortOrder <= formCompletedStep || isReadOnly,
            }"
          />
        </template>
      </v-stepper-header>
      <v-divider class="mb-6" />
      <!--Content/Tabs-->
      <v-stepper-window v-model="selectedStepOrder">
        <v-stepper-window-item
          v-for="step in form?.steps"
          :key="`${step.uniqueId}-content`"
          :value="step.sortOrder"
        >
          <v-row
            v-if="step.sortOrder === selectedStepOrder"
            class="stepper-content"
            no-gutters
          >
            <v-col
              cols="12"
              md="2"
            >
              <v-tabs
                v-model="activeTab"
                direction="vertical"
                class="vertical-slider-line flex-column"
                grow
              >
                <!-- Side Tabs -->
                <template
                  v-for="(tabId, index) in getVisibleTabs(step)"
                  :key="tabId"
                >
                  <v-tab
                    :value="tabId"
                    class="tab-header text-wrap"
                    :disabled="!isReadOnly"
                  >
                    {{ getTabName(tabId) }}
                    <span class="tab-numbers">
                      {{ index + 1 }}/{{ getVisibleTabs(step).length }}
                    </span>
                  </v-tab>
                </template>
              </v-tabs>
            </v-col>

            <v-col
              cols="12"
              md="9"
            >
              <!-- Tabs Content -->
              <v-window
                v-model="activeTab"
                :transition="false"
                :reverse-transition="false"
                class="flex-grow-1 pa-4"
              >
                <v-window-item
                  v-for="tabId in getVisibleTabs(step)"
                  :key="`${tabId}-content`"
                  :value="tabId"
                  class="tab-content"
                >
                  <div class="form-title">
                    {{ getTabName(tabId) }}
                  </div>
                  <div class="pa-2" />
                  <div v-if="!isReadOnly">
                    <!-- PatientPicker -->
                    <PatientPickerWindow
                      v-if="
                        getOrderFormTabById(tabId)?.flag ===
                          formFlags.isPatientInfo ||
                          getOrderFormTabById(tabId)?.flag ===
                          formFlags.isParentConsent
                      "
                      :flag="getOrderFormTabById(tabId)?.flag || ''"
                      @patientSelected="fillPatientData($event)"
                      @parentSelected="fillParentData($event)"
                    />

                    <OrderSenderData
                      v-if="getOrderFormTabById(tabId)?.flag === formFlags.isSenderInfo"
                      :sender-data="senderData"
                      @fillSender="fillSenderData($event)"
                    />

                    <OrderInvoiceData
                      v-if="getOrderFormTabById(tabId)?.flag === formFlags.isInvoiceInfo"
                      :sender-data="senderData"
                      @fillInvoice="fillInvoiceData($event)"
                    />
                  </div>

                  <!-- Form -->
                  <v-form
                    :ref="el => (formRefs[tabId] = el)"
                    v-model="step.valid"
                    :disabled="isReadOnly"
                    validation="lazy"
                  >
                    <template
                      v-for="controlId in getOrderFormTabById(tabId)?.controls"
                      :key="controlId"
                    >
                      <ControlRenderer
                        :tab-id="tabId"
                        :control-id="controlId"
                      />
                    </template>
                  </v-form>

                  <!-- Footer -->
                  <v-container class="tab-footer">
                    <Button
                      color="secondary text-red"
                      class="cancel-order-button"
                      @click="cancelOrder()"
                    >
                      {{ $t("Cancel order") }}
                    </Button>
                    <Button
                      v-show="!isFirstBackButton(step, tabId)"
                      class="ml-sm-3"
                      color="secondary"
                      :scroll="true"
                      @click="previousTab(step.sortOrder)"
                    >
                      {{ $t("Back") }}
                    </Button>
                    <Button
                      class="ml-sm-3"
                      :scroll="true"
                      @click="nextTab(step.sortOrder)"
                    >
                      {{ $t(nextButtonTitle(step.sortOrder)) }}
                      <v-icon
                        class="text-white icon-flipped"
                        end
                      >
                        mdi-keyboard-backspace
                      </v-icon>
                    </Button>
                  </v-container>
                </v-window-item>
              </v-window>
            </v-col>
          </v-row>
        </v-stepper-window-item>
      </v-stepper-window>
    </v-stepper>
  </v-container>
</template>

<script lang="ts" setup>
import { ref, computed, watch, reactive, onMounted, nextTick, toRaw } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useFormStore } from '@/stores/form'

import Button from "@/components/input-components/Button.vue";
import ControlRenderer from "@/components/ControlRenderer.vue";
import PatientPickerWindow from "@/components/patients/PatientPickerWindow.vue";
import OrderSenderData from "@/components/orders/OrderSenderData.vue";
import OrderInvoiceData from "@/components/orders/OrderInvoiceData.vue";

import { FORM_CONTROLS, FORM_FLAGS, ORDER_STATUS } from "@/utils/constants";
import { FormStep } from "@/entities/form-step";
import { FormTab } from "@/entities/form-tab";
import { JsonForm } from "@/entities/json-form";
import { Patient } from "@/entities/patient";
import { User } from "@/entities/user";
import NotificationMessageService from "@/services/notificationmessage.service";
import { convertFrontendSexValueToFormBuilderValue } from '@/utils'
import { useOrdersStore } from '@/stores/orders';
import { useControlRenderer } from '@/composables/useControlRenderer'
import { usePermissionsService } from "@/composables/usePermissionsService";
import {storeToRefs} from "pinia";
import {FormControl} from "@/entities/form-control";
import {ProfileService} from "@/services/profile.service";

type ParentPrefix = 'parent1' | 'parent2';
const { showTab } = useControlRenderer()

const service = new ProfileService()
const ordersStore = useOrdersStore();
const permissionsService = usePermissionsService();
// Emits
const emit = defineEmits([
  'cancelOrder',
  'saveOrder',
  'showSummary',
  'patientSelected'
])

const getOrderFormTabById = ordersStore.getOrderFormTabById
const getOrderFormStepBySortOrder = ordersStore.getOrderFormStepBySortOrder
const { order, orderPatient } = storeToRefs(ordersStore)
const formStore = useFormStore()
const { t, locale } = useI18n()
const route = useRoute()

const id = route.params.id

const activeTab = ref<string>('')
const devTesting = ref(false)

const formRefs = reactive<Record<string, any>>({})

const formFlags = FORM_FLAGS
const form = computed<JsonForm | undefined>(() => ordersStore.getOrderForm)
const selectedStepOrder = ref(form.value?.steps?.[0]?.sortOrder ?? 1)

const formCompletedStep = computed(() => form.value?.formConfig?.completedStep || 0)
const isViewOrder = computed(() => route.fullPath.split('/')[2]?.toLowerCase() === 'view')
const isReadOnly = computed(() =>
  (!permissionsService.hasOrderUpdateAllPermission && order.value.statusId !== ORDER_STATUS.draft) || isViewOrder.value
)
const areControlsReady = computed(() => {
  return !!ordersStore.getOrderFormControls?.length
})

const isFormLoaded = ref(false)
const doctorId = computed(() => order.value?.doctorId)
const senderData = ref<User | null>(null)

function setActiveTabForStep(stepOrder: number) {

  const step = getOrderFormStepBySortOrder(stepOrder)
  if (!step) return
  const visibleTabs = getVisibleTabs(step)

  activeTab.value = visibleTabs.length > 0 ? visibleTabs[0] : ''
}

function isFirstBackButton(step: FormStep, tabId: string): boolean {
  const visible = getVisibleTabs(step)
  return step.sortOrder === 1 && tabId === visible[0]
}

function stepCompleted(step: FormStep): boolean {
  if (step?.tabs && step.tabs.length > 0) {
    return step.valid || isReadOnly.value
  }
  return true
}

async function previousTab(currOrder: number) {
  const step = getOrderFormStepBySortOrder(currOrder)
  if (!step) return

  const visibleTabs = getVisibleTabs(step)
  const idx = visibleTabs.indexOf(activeTab.value)
  if (idx === -1) return

  if (idx > 0) {
    activeTab.value = visibleTabs[idx - 1]
    await nextTick()
    scrollToTop()
    return
  } else {
    previousStep(currOrder)
    await nextTick()
    scrollToTop()
  }
}

async function nextTab(currOrder: number) {
  const step = getOrderFormStepBySortOrder(currOrder);
  if (!step) return;

  const visibleTabs = getVisibleTabs(step);
  const currentIdx = visibleTabs.indexOf(activeTab.value);
  if (currentIdx === -1) return;

  // Validate current tab before moving
  const valid = await validateFormTab(activeTab.value);
  if (!valid) {
    scrollToFirstError(activeTab.value);
    return;
  }

  // If last tab in this step, move to next step or summary
  if (currentIdx === visibleTabs.length - 1) {
    const stepValid = await validateStep(currOrder);
    formStore.setStepValidity(currOrder, stepValid);
    nextStep(currOrder);
  } else {
    // Move to the next tab within the same step
    activeTab.value = visibleTabs[currentIdx + 1];
    await nextTick();
    scrollToTop();
  }
}

function getTabName(tabId: string): string {
  const formTab = getOrderFormTabById(tabId);

  if (!formTab) {
    return '';
  }

  return getName(formTab);
}

async function validateTabs(tabs: string[]): Promise<boolean> {
  let valid = true;
  for (const tabId of tabs) {
    const ref = formRefs[tabId];
    if (ref?.validate) {
      const r = await ref.validate();
      if (!r.valid) {
        valid = false;
        break;
      }
    }
  }
  return valid;
}

async function validateStep(stepOrder: number): Promise<boolean> {
  const step = getOrderFormStepBySortOrder(stepOrder)
  if (!step) return true

  const tabs = getVisibleTabs(step);
  const stepValid = await validateTabs(tabs);

  formStore.setStepValidity(stepOrder, stepValid);
  return stepValid;
}

function nextStep(currOrder: number) {
  const lastStep = getLastStep();
  if (currOrder < lastStep) {
    const next = getOrderFormStepBySortOrder(currOrder + 1);
    if (!next) return;
    selectedStepOrder.value = currOrder + 1;
    const visibleTabs = getVisibleTabs(next);
    activeTab.value = visibleTabs[0] || '';
    scrollToTop();
    return;
  }
  if (!areAllStepsValid()) {
    NotificationMessageService.showError("One of the steps was not filled out completely.");
    return;
  }
  emit("showSummary");
  if (selectedStepOrder.value > 0 && "formConfig" in form.value) {
    form.value.formConfig.completedStep = selectedStepOrder.value - 1;
  }
}

function previousStep(currOrder: number) {
  if (currOrder <= 1) return
  const prev = getOrderFormStepBySortOrder(currOrder - 1)

  if (prev) {
    selectedStepOrder.value = currOrder - 1
    const visibleTabs = getVisibleTabs(prev)
    activeTab.value = visibleTabs[visibleTabs.length - 1] || ''
  }
}

function onStepChange(): void {
  if (!isReadOnly.value) emit('saveOrder')
}

function areAllStepsValid(): boolean {
  const currentForm = form.value

  if (!currentForm) {
    return false
  }

  return currentForm.steps.every((step) => step.valid)
}

async function validateFormTab(tabId: string): Promise<boolean> {
  if (devTesting.value) return true

  const formComponent = formRefs[tabId]
  if (!formComponent?.validate) return true

  const result = await formComponent.validate()

  const currentStep = selectedStepOrder.value

  if (!result.valid) {
    formStore.setStepValidity(currentStep, false);
  } else {
    const step = getOrderFormStepBySortOrder(currentStep);
    if (step) {
      const tabs = getVisibleTabs(step)

      const stepValid = await validateTabs(tabs)
      formStore.setStepValidity(currentStep, stepValid)
    }
  }

  if (result.valid && !isReadOnly.value) emit('saveOrder')

  return result.valid
}

function isEditable(stepOrder: number): boolean {
  return stepOrder <= formCompletedStep.value + 1 || isReadOnly.value
}

function isGreen(stepOrder: number): boolean {
  return (
    (stepOrder <= formCompletedStep.value || isReadOnly.value) &&
    stepOrder !== selectedStepOrder.value
  )
}

function isLastStep(stepOrder: number): boolean {
  return stepOrder === getLastStep()
}

function isLastTab(stepOrder: number): boolean {
  const step = getOrderFormStepBySortOrder(stepOrder);
  if (!step) return false;
  const visibleTabs = getVisibleTabs(step);
  const currentIdx = visibleTabs.indexOf(activeTab.value);
  return currentIdx === visibleTabs.length - 1;
}

function nextButtonTitle(sortOrder: number): string {
  return isLastStep(sortOrder) && isLastTab(sortOrder)
    ? 'Review Order'
    : 'Next'
}

function getVisibleTabs(step?: FormStep | null): string[] {
  if (!step?.tabs || !Array.isArray(step.tabs)) {
    return [];
  }

  return step.tabs.filter((tab: string) => {
    const formTab = getOrderFormTabById(tab);

    if (!formTab) {
      return false;
    }

    return showTab(formTab);
  });
}

function getLastStep(): number {
  return Math.max(...form.value?.steps.map((s) => s.sortOrder), 1)
}

function getName(object: FormStep | FormTab): string {
  const localization = object.localizations[locale.value]
  return locale.value !== 'en' && localization?.name ? localization.name : object.name
}

function setFormControlValue(formControlName: string, value: any) {
  const control = ordersStore.getOrderFormControlByName(formControlName)

  if (control) {
    control.value = value ?? null
  }
}

function setFormTabControlValue(tabId: string, controlName: string, value: any): void {
  const control = findTabControl(tabId, controlName)

  if (control) control.value = value ?? null
}

function findTabControl(tabId: string, controlName: string): FormControl | null {
  const tab = getOrderFormTabById(tabId)
  if (!tab) return null
  const controls = ordersStore.order?.form?.controls
  if (!controls) return null

  return controls.find(control =>
    control.name === controlName && tab.controls.includes(control.uniqueId)
  ) ?? null
}

function getParentPrefixByTab(tabId: string): ParentPrefix | null {
  const tab = getOrderFormTabById(tabId);
  if (!tab) return null;
  const controls = ordersStore.order?.form?.controls;
  if (!controls) return null;

  let hasParent1 = false;
  let hasParent2 = false;

  for (const controlId of tab.controls) {
    const control = controls.find(c => c.uniqueId === controlId)
    if (!control) continue
    const name = control.name
    if (name === FORM_CONTROLS.parent1_firstName || name === FORM_CONTROLS.parent1_lastName ||
      name === FORM_CONTROLS.parent1_dateOfBirth || name === FORM_CONTROLS.parent1_sexId) {
      hasParent1 = true
    }
    if (name === FORM_CONTROLS.parent2_firstName || name === FORM_CONTROLS.parent2_lastName ||
      name === FORM_CONTROLS.parent2_dateOfBirth || name === FORM_CONTROLS.parent2_sexId) {
      hasParent2 = true
    }
  }

  if (hasParent1) return 'parent1'
  if (hasParent2) return 'parent2'
  return null
}

function formatDateForInput(date: string | null) {
  if (!date) return null

  // ISO
  if (date.includes('T')) return date.split('T')[0]

  // dd.MM.yyyy → ISO
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(date)) {
    const [day, month, year] = date.split('.')
    return `${year}-${month}-${day}`
  }

  return date
}

function mapPatientToOrder(patient: Patient) {
  if (order.value) {
    order.value.patientId = patient.patientId
    order.value.patientFirstName = patient.firstName
    order.value.patientLastName = patient.lastName
    order.value.patientDateOfBirth = patient.dateOfBirth
    order.value.patientSexId = patient.sexId
    order.value.cegatId = patient.patientNumber
    order.value.referenceId = patient.externalId || ''
  }
}

function mapPatientToForm(patient: Patient) {
  setFormControlValue(FORM_CONTROLS.patient_firstName, patient.firstName)
  setFormControlValue(FORM_CONTROLS.patient_lastName, patient.lastName)
  setFormControlValue(FORM_CONTROLS.patient_dateOfBirth, formatDateForInput(patient.dateOfBirth))
  setFormControlValue(FORM_CONTROLS.patient_sexId, convertFrontendSexValueToFormBuilderValue(patient.sexId))
  setFormControlValue(FORM_CONTROLS.patient_externalId, patient.externalId)
}

function fillPatientData(patient: Patient) {
  if (!patient) return
  mapPatientToOrder(patient);
  mapPatientToForm(patient);
  emit('patientSelected', patient)
  nextTick(() => emit('saveOrder'))
}

function fillParentData(patient: Patient): void {
  if (!patient) return
  const currentTabId = activeTab.value
  if (!currentTabId) return
  const parentPrefix = getParentPrefixByTab(currentTabId)
  if (!parentPrefix) return

  const fields = {
    firstName: patient.firstName,
    lastName: patient.lastName,
    dateOfBirth: formatDateForInput(patient.dateOfBirth),
    sexId: convertFrontendSexValueToFormBuilderValue(patient.sexId),
  };

  Object.entries(fields).forEach(([key, value]) => {
    const controlName = FORM_CONTROLS[`${parentPrefix}_${key}`]
    setFormTabControlValue(currentTabId, controlName, value)
  })

  nextTick(() => emit('saveOrder'))
}

function fillSenderData(sender: User): void {
  if (!sender) return

  setFormControlValue(FORM_CONTROLS.sender_firstName, sender.firstName)
  setFormControlValue(FORM_CONTROLS.sender_lastName, sender.lastName)
  setFormControlValue(FORM_CONTROLS.sender_physicianNpiNumber, sender.physicianNpiNumber)
  setFormControlValue(FORM_CONTROLS.sender_institution, sender.institution)
  setFormControlValue(FORM_CONTROLS.sender_street, sender.street)
  setFormControlValue(FORM_CONTROLS.sender_postalCode, sender.postalCode)
  setFormControlValue(FORM_CONTROLS.sender_city, sender.city)
  setFormControlValue(FORM_CONTROLS.sender_country, sender.country)
  setFormControlValue(FORM_CONTROLS.sender_email, sender.email)
}

function fillInvoiceData(invoice: any): void {
  if (!invoice) return

  setFormControlValue(FORM_CONTROLS.firstName, invoice.firstName)
  setFormControlValue(FORM_CONTROLS.lastName, invoice.lastName)
  setFormControlValue(FORM_CONTROLS.street, invoice.street)
  setFormControlValue(FORM_CONTROLS.postalCode, invoice.postalCode)
  setFormControlValue(FORM_CONTROLS.city, invoice.city)
  setFormControlValue(FORM_CONTROLS.country, invoice.country)
  setFormControlValue(FORM_CONTROLS.email, invoice.email)
}

function cancelOrder(): void {
  emit('cancelOrder')
}

function scrollToTop() {
  nextTick(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  })
}

function scrollToFirstError(tabId: string) {
  nextTick(() => {
    const formComponent = formRefs[tabId]
    if (!formComponent?.$el) return

    const invalid = formComponent.$el.querySelector(
      '.v-field--error, .v-input--error'
    ) as HTMLElement | null

    if (!invalid) return

    const field = invalid.closest('.v-field, .v-input') as HTMLElement | null
    const target = field || invalid

    target.scrollIntoView({ behavior: 'smooth', block: 'center' })

    invalid.focus()
  })
}

watch(form, (f) => {
  isFormLoaded.value = !!f?.steps && f.steps.length > 0
}, { immediate: true })

watch([() => isFormLoaded.value, () => selectedStepOrder.value], ([loaded, step]) => {
  if (!loaded) return
  setActiveTabForStep(step)
}, { immediate: true })

watch(
  [
    () => orderPatient.value?.patientId,
    () => areControlsReady.value,
    () => isFormLoaded.value
  ],
  async ([orderPatientId, controlsReady, formLoaded]) => {
    if (!route.fullPath.toLowerCase().includes('/order/new')) return
    if (!controlsReady || !formLoaded) return
    if (!orderPatientId || !orderPatient.value) return

    await nextTick()

    mapPatientToOrder(orderPatient.value)
    mapPatientToForm(orderPatient.value)
  },
  { immediate: true }
)

watch(doctorId, async (newId) => {
  if (!newId) {
    senderData.value = null
    return
  }
  try {
    const data = await service.getProfile(newId)
    if (doctorId.value === newId) {
      senderData.value = data
    }
  } catch {
    senderData.value = null
  }
}, { immediate: true })

watch(
  () => {
    const step = getOrderFormStepBySortOrder(selectedStepOrder.value)
    return step ? getVisibleTabs(step) : []
  },
  (tabs) => {
    if (!tabs.length) return
    if (!tabs.includes(activeTab.value)) {
      activeTab.value = tabs[0]
    }
  },
  { flush: 'post' }
)

onMounted(() => {
  // start draft order on next not yet completed step
  if (formCompletedStep.value > 0 && !isReadOnly.value) {
    selectedStepOrder.value = formCompletedStep.value + 1
  }

  // fill patient data of an order started from within patient's list
  // if (order.value?.orderId === 0 && orderPatient.value.patientId) {
  //   fillPatientData(orderPatient.value)
  // }
})
</script>
<!--<style lang="scss" scoped>-->
<!--.vertical-slider-line .v-tabs__slider,-->
<!--.vertical-slider-line .v-tab__slider {-->
<!--  transition: none !important;-->
<!--}-->
<!--</style>-->
